import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { useDrag, useDrop, DragSourceMonitor, DropTargetMonitor } from 'react-dnd';
import { useStorage } from '~Components/Storage';
import { useAdyen, useButton, useI18n, useLogger } from '~Hooks';
import Toggle from '~Components/Toggle';
import Modal from '~ComponentsImproved/Modal';
import { ModalContent } from '~Styles/modal';
import { DragTypes } from '~ComponentsImproved/TSR/Types';

import { BaseInputStyling, BlueButton, Box } from '~Styles';
import Heading from '~ComponentsImproved/Typography/Heading';

const Wrapper = styled.div<{ isPrimary: boolean; isDragging: boolean }>`
  position: relative;
  background-color: white;
  padding: 8px 16px 8px 40px;
  flex: 1;
  border-radius: 3px;
  border: 2px solid ${({ isPrimary }) => (isPrimary ? 'var(--colorUIYellow)' : 'var(--colorBlueGray300)')};
  text-align: center;
  margin-bottom: 4px;
  color: var(--colorBlueGray1200);
  opacity: ${({ isDragging }) => (isDragging ? 0 : 1)};
`;

const DragHandle = styled.div`
  position: absolute;
  left: 4px;
  top: 50%;
  transform: translateY(-50%);
  cursor: grab;
  color: var(--colorBlueGray600);
  font-size: 18px;
  padding: 8px;
  user-select: none;
`;

const InnerContent = styled.div``;

const TerminalRow = styled.div`
  margin: 15px 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
`;

const TerminalRowCell = styled.div`
  text-align: left;
  flex: 0 0 auto;
  margin: 0 15px;
`;

const RemoveIcon = styled.i`
  margin-left: auto;
  font-weight: bold;
  :active {
    opacity: 0.5;
  }
`;

const TerminalName = styled.div`
  font-size: 18px;
  font-weight: bold;
`;

const NameInput = styled.input`
  ${BaseInputStyling}
  padding: 8px 16px;
`;

const PrimaryBadge = styled.span`
  display: inline-block;
  background-color: var(--colorUIYellow);
  color: var(--colorBlack);
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 600;
  margin-left: 8px;
  vertical-align: middle;
`;

interface Props {
  id: string;
  index: number;
  isPrimary: boolean;
  onRemove: () => void;
  onMoveTerminal: (dragIndex: number, hoverIndex: number) => void;
}

export interface TerminalDragItem {
  type: string;
  index: number;
  id: string;
  displayName: string | undefined;
}

const Terminal: React.FunctionComponent<Props> = ({ id, index, isPrimary, onRemove, onMoveTerminal }) => {
  const adyen = useAdyen();
  const logger = useLogger('adyen-settings');
  const [data, updateStorage] = useStorage();
  const adyenTerminals = data.adyenTerminals;
  const terminal = adyenTerminals.find((at) => at.POIID === id)!;
  const { i18n } = useI18n();
  const [showModal, setShowModal] = useState(false);
  const [name, setName] = useState('');
  const isHandy = window.innerWidth <= 600;
  const ref = useRef<HTMLDivElement>(null);
  const prevTopRef = useRef<number | null>(null);

  const terminalName = useMemo(() => {
    const { terminalNames } = data;
    return terminalNames.find((t) => t.id === terminal.POIID)?.name;
  }, [data.terminalNames]);

  // Drag-and-drop for reordering terminals
  const [{ isDragging }, drag, preview] = useDrag({
    item: { type: DragTypes.TERMINAL, index, id, displayName: terminalName },
    collect: (monitor: DragSourceMonitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: DragTypes.TERMINAL,
    hover: (draggedItem: TerminalDragItem, monitor: DropTargetMonitor) => {
      if (!ref.current) return;
      const dragIndex = draggedItem.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;

      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) return;
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      // Only move when the cursor has crossed half of the item height
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

      onMoveTerminal(dragIndex, hoverIndex);
      draggedItem.index = hoverIndex;
    },
  });

  // Combine refs: drop on the whole card, drag only on the handle
  drop(preview(ref));

  // FLIP animation: animate position changes when items reorder
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || isDragging) {
      prevTopRef.current = el?.getBoundingClientRect().top ?? null;
      return;
    }

    const newTop = el.getBoundingClientRect().top;
    const prevTop = prevTopRef.current;
    prevTopRef.current = newTop;

    if (prevTop === null) return;
    const deltaY = prevTop - newTop;
    if (deltaY === 0) return;

    // Instantly offset to old position, then animate to new position
    el.style.transition = 'none';
    el.style.transform = `translateY(${deltaY}px)`;

    requestAnimationFrame(() => {
      el.style.transition = 'transform 200ms ease';
      el.style.transform = '';
    });
  });

  const certificateButton = useButton({
    onClick: () => handleClick(terminal.IP!),
  });
  const testButton = useButton({
    onClick: () => handleTest(terminal.POIID),
  });
  const setNameButton = useButton({
    onClick: () => handleSetName(),
  });

  const handleClick = (ip: string) => {
    if (window.hasNativeWrapper) {
      return window.postMessageReact(`https://${ip}:8443/nexo`);
    }

    return window.open(`https://${ip}:8443/nexo`);
  };

  /**
   * Test the connection between the device and the terminal.
   * Mostly to aid when installing, so they dont have to make a payment
   */
  const handleTest = async (poiid: string) => {
    try {
      const result = await adyen.prompt(poiid, 'Connection Test', [
        {
          id: 'ok',
          title: 'Confirm',
        },
      ]);

      window.alert('Connection success');
    } catch (e) {
      let message = (e as Error).message;

      if (message.includes('Failed to send message')) {
        message = i18n('Adyen.TerminalOffline');
      }

      if (message.includes('Unknown ')) {
        message = i18n('Adyen.WrongTerminalId');
      }

      logger.warn((e as Error).message);

      window.alert(message);
    }
  };

  const updateHasPrinter = (terminalPOIID: string, hasPrinter: boolean) => {
    updateStorage({
      adyenTerminals: adyenTerminals.map((storedTerminal) => {
        if (storedTerminal.POIID === terminalPOIID) {
          return {
            ...storedTerminal,
            hasPrinter,
          };
        }

        return storedTerminal;
      }),
    });
  };

  const handleSetName = () => {
    if (showModal) {
      const id = terminal.POIID;
      const filtered = data.terminalNames.filter((t) => t.id !== id);
      if (name === '') {
        updateStorage({
          terminalNames: filtered,
        });
      } else {
        updateStorage({
          terminalNames: [...filtered, { id, name }],
        });
      }
      setShowModal(false);
      setName('');
    } else {
      setShowModal(true);
    }
  };

  return (
    <Wrapper ref={ref} isPrimary={isPrimary} isDragging={isDragging}>
      <DragHandle ref={drag}>&#9776;</DragHandle>
      <InnerContent>
        <TerminalRow>
          {terminalName && (
            <TerminalRowCell>
              <div>{i18n('Settings.Terminal.Name')}</div>
              <TerminalName>{terminalName}</TerminalName>
            </TerminalRowCell>
          )}
          <TerminalRowCell>
            <div>{i18n('Settings.TerminalID')}</div>
            <b>{terminal.POIID}</b>
            {isPrimary && <PrimaryBadge>{i18n('Settings.Terminal.Primary')}</PrimaryBadge>}
          </TerminalRowCell>
        </TerminalRow>

        <TerminalRow>
          {terminal.IP && (
            <TerminalRowCell>
              <BlueButton {...certificateButton}>{i18n('Settings.AddCert')}</BlueButton>
            </TerminalRowCell>
          )}
          <TerminalRowCell>
            <BlueButton {...testButton}>{i18n('Settings.Test')}</BlueButton>
          </TerminalRowCell>
          {!isHandy && (
            <TerminalRowCell>
              <BlueButton {...setNameButton}>{i18n('Settings.TerminalSetName')}</BlueButton>
            </TerminalRowCell>
          )}
          <TerminalRowCell>
            <Toggle
              label={i18n('Settings.Printer')}
              value={terminal.hasPrinter}
              onClick={(value) => updateHasPrinter(terminal.POIID, value)}
            />
          </TerminalRowCell>
          <RemoveIcon className="icon cross" onClick={onRemove} />
          <TerminalRowCell />
        </TerminalRow>
      </InnerContent>
      <Modal isShowing={showModal} hide={() => setShowModal(!showModal)} position="flex-start">
        <ModalContent>
          <Heading level={2} color="#fff" bottom={16}>
            {i18n('Settings.TerminalEnterName')}
          </Heading>
          <Box marginBottom="medium">
            <NameInput
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          </Box>
          <BlueButton {...setNameButton}>{i18n('Settings.TerminalSaveName')}</BlueButton>
        </ModalContent>
      </Modal>
    </Wrapper>
  );
};

export default Terminal;
